import { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { api, ApiError } from "../../lib/api";
import { slugFromSubject, slugifySubject } from "../../lib/studySubjects";
import { useStudySubjects } from "../../lib/useStudySubjects";

import { StatusMessage } from "../../components/ui/StatusMessage";

/** Resolves legacy /student/exams/:examId links into the study flow. */
export function LegacyExamRedirect() {
  const { examId } = useParams<{ examId: string }>();
  const { tokens } = useAuth();
  const token = tokens?.accessToken ?? null;
  const { subjects } = useStudySubjects();
  const [target, setTarget] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!token || !examId) return;
    api.student.exams.get(token, examId)
      .then((exam) => {
        const subject = exam.subject ?? "General";
        const slug = slugFromSubject(subject, subjects) || slugifySubject(subject);
        setTarget(`/student/study/${slug}/exams/${examId}`);
      })
      .catch(() => setError(true));
  }, [token, examId, subjects]);

  if (!examId) return <Navigate to="/student/study" replace />;
  if (target) return <Navigate to={target} replace />;
  if (error) {
    return (
      <div className="py-12 px-4 max-w-md mx-auto">
        <StatusMessage
          variant="warning"
          title="Exam unavailable"
          message="We could not open this exam. Please try again."
          action={{
            label: "Back to study",
            href: "/student/study",
          }}
        />
      </div>
    );
  }
  return (
    <div className="py-24 flex flex-col items-center gap-2 text-muted-foreground">
      <Loader2 className="animate-spin text-primary" />
      <span className="text-sm">Opening exam…</span>
    </div>
  );
}
